import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Phone, 
  PhoneIncoming,
  Clock, 
  User, 
  FileText, 
  AlertCircle,
  CheckCircle,
  Video,
  Timer,
  Volume2,
  Loader2,
  Key,
  XCircle
} from 'lucide-react';
import { BrandingProvider, useBranding } from '../../contexts/BrandingContext';
import { 
  BrandedHeader, 
  BrandedButton, 
  BrandedCard, 
  BrandedBadge 
} from '../../components/branding/BrandedComponents';
import { useAuth } from '../../contexts/AuthContext';

// Real request API integration
interface PendingRequest {
  requestId: string;
  clientName: string;
  companyName: string;
  caseType: string;
  requestType: string;
  urgency: 'high' | 'medium' | 'low';
  status: string;
  createdAt: string;
  expiresAt: string;
  waitTime: number; // in minutes
  metadata: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
    notes?: string;
    templateType?: string;
    requestType?: string;
  };
}

interface GeneralAgentDashboardProps {
  agentName?: string;
  companyName?: string;
  onAcceptClient?: (clientId: string) => void;
  onRejectClient?: (clientId: string) => void;
}

export const GeneralAgentDashboard: React.FC<GeneralAgentDashboardProps> = ({
  agentName = 'Agent',
  companyName = 'Customer Connect',
  onAcceptClient,
  onRejectClient
}) => {
  return (
    <BrandingProvider templateType="general" initialBranding={{ companyName: companyName }}>
      <GeneralAgentDashboardContent 
        agentName={agentName}
        companyName={companyName}
        onAcceptClient={onAcceptClient}
        onRejectClient={onRejectClient}
      />
    </BrandingProvider>
  );
};

const GeneralAgentDashboardContent: React.FC<GeneralAgentDashboardProps> = ({
  agentName = 'Agent',
  companyName = 'Customer Connect',
  onAcceptClient
}) => {
  const { getCurrentOrganizationId, isAuthenticated } = useAuth();
  const { branding } = useBranding();
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState<string | null>(null);
  const [isRejecting, setIsRejecting] = useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  // Audio context for notification sounds
  const audioContextRef = useRef<AudioContext | null>(null);
  
  // Initialize audio context
  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);
  
  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (!soundEnabled) return;
    
    try {
      const audioContext = initAudio();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // High-pitched notification beep
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.3);
      
      console.log('🔊 AGENT: Notification sound played');
    } catch (error) {
      console.error('🔇 AGENT: Failed to play sound:', error);
    }
  }, [soundEnabled, initAudio]);
  
  // Get organization ID for proper tenant isolation
  const orgId = getCurrentOrganizationId();
  
  // Log current state for debugging  
  console.log('🔐 AGENT: Current organization ID:', orgId);
  console.log('🔑 AGENT: Is authenticated:', isAuthenticated);
  
  // Keep track of current requests for notification detection
  const currentRequestIdsRef = useRef<Set<string>>(new Set());

  // Fetch pending requests from API
  const fetchPendingRequests = useCallback(async (isInitial = false) => {
    if (!isAuthenticated || !orgId) {
      console.log('⚠️ AGENT: Not authenticated or no org ID, skipping fetch');
      if (isInitial) {
        setIsInitialLoading(false);
      }
      return;
    }
    
    console.log(`🔍 AGENT: Fetching pending requests... (${isInitial ? 'initial' : 'refresh'})`);
    if (isInitial) {
      setIsInitialLoading(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/general/consultations/pending?organizationId=${orgId}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('🔍 AGENT: API Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('📋 AGENT: Received pending requests:', data);
        
        const allRequests = data.consultations || [];
        // Filter for general template requests only
        const newRequests = allRequests.filter((r: PendingRequest) => 
          r.metadata?.templateType === 'general' || r.caseType === 'general_inquiry'
        );
        
        // Check for new requests and play sound using ref to avoid dependency
        const newRequestIds = new Set<string>(newRequests.map((r: PendingRequest) => r.requestId));
        const hasNewRequests = newRequests.some((r: PendingRequest) => !currentRequestIdsRef.current.has(r.requestId));
        if (hasNewRequests && !isInitial && currentRequestIdsRef.current.size > 0) {
          console.log('🔔 AGENT: New request detected, playing notification sound');
          playNotificationSound();
        }
        
        // Update current request IDs ref
        currentRequestIdsRef.current = newRequestIds;
        setPendingRequests(newRequests);
      } else if (response.status === 404) {
        console.log('📭 AGENT: No pending requests found');
        currentRequestIdsRef.current = new Set();
        setPendingRequests([]);
      } else {
        const errorText = await response.text();
        console.error('🔴 AGENT: Error fetching requests:', errorText);
        setError(`Failed to fetch requests: ${response.status}`);
      }
    } catch (err) {
      console.error('🔴 AGENT: Network error fetching requests:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch pending requests');
    } finally {
      setIsLoading(false);
      if (isInitial) {
        setIsInitialLoading(false);
      }
    }
  }, [isAuthenticated, orgId, playNotificationSound]);

  // Accept a request
  const acceptRequest = useCallback(async (requestId: string) => {
    console.log('✅ AGENT: Accepting request:', requestId);
    setIsAccepting(requestId);
    setError(null);

    try {
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/general/consultations/${requestId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          agentName: agentName,
          companyName: companyName || 'Customer Connect',
          organizationId: orgId
        })
      });

      console.log('✅ AGENT: Accept response status:', response.status);

      if (response.ok) {
        const acceptData = await response.json();
        console.log('🎉 AGENT: Request accepted successfully:', acceptData);
        
        // Remove from pending requests immediately for better UX
        setPendingRequests(prev => prev.filter(r => r.requestId !== requestId));
        
        // Call the onAcceptClient callback if provided
        onAcceptClient?.(requestId);
        
        // Navigate to session room
        const roomId = acceptData.roomId || acceptData.meetingId;
        if (roomId) {
          const sessionUrl = `${window.location.origin}/${orgId}/templates/general/session/host?roomId=${roomId}&agentName=${encodeURIComponent(agentName)}&requestId=${requestId}&participantType=host`;
          console.log('🔗 AGENT: Redirecting to session room:', sessionUrl);
          window.location.href = sessionUrl;
        } else {
          console.error('🔴 AGENT: No room ID received from accept response');
        }
        
        // Refresh the list after a short delay
        setTimeout(() => fetchPendingRequests(false), 1000);
      } else {
        const errorText = await response.text();
        console.error('🔴 AGENT: Error accepting request:', errorText);
        setError(`Failed to accept request: ${response.status}`);
      }
    } catch (err) {
      console.error('🔴 AGENT: Network error accepting request:', err);
      setError(err instanceof Error ? err.message : 'Failed to accept request');
    } finally {
      setIsAccepting(null);
    }
  }, [agentName, orgId, onAcceptClient, fetchPendingRequests]);

  // Reject a request
  const rejectRequest = useCallback(async (requestId: string) => {
    console.log('❌ AGENT: Rejecting request:', requestId);
    setIsRejecting(requestId);
    setError(null);

    try {
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/general/consultations/${requestId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          agentName: agentName,
          organizationId: orgId,
          reason: 'Agent declined'
        })
      });

      console.log('❌ AGENT: Reject response status:', response.status);

      if (response.ok) {
        console.log('🗑️ AGENT: Request rejected successfully');
        // Remove from pending requests immediately for better UX
        setPendingRequests(prev => prev.filter(r => r.requestId !== requestId));
        
        // Refresh the list after a short delay
        setTimeout(() => fetchPendingRequests(false), 1000);
      } else {
        const errorText = await response.text();
        console.error('🔴 AGENT: Error rejecting request:', errorText);
        setError(`Failed to reject request: ${response.status}`);
      }
    } catch (err) {
      console.error('🔴 AGENT: Network error rejecting request:', err);
      setError(err instanceof Error ? err.message : 'Failed to reject request');
    } finally {
      setIsRejecting(null);
    }
  }, [orgId, fetchPendingRequests]);

  // Initial load and polling setup
  useEffect(() => {
    if (!isAuthenticated || !orgId) {
      console.log('⚠️ AGENT: Not authenticated or no org ID, skipping setup');
      setIsInitialLoading(false);
      return;
    }

    let isCancelled = false;

    const performFetch = async (isInitial: boolean) => {
      if (isCancelled) return;
      
      console.log(`🔍 AGENT: Fetching pending requests... (${isInitial ? 'initial' : 'refresh'})`);
      if (isInitial) {
        setIsInitialLoading(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      try {
        const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/general/consultations/pending?organizationId=${orgId}`, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (isCancelled) return;

        console.log('🔍 AGENT: API Response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          console.log('📋 AGENT: Received pending requests:', data);
          
          if (!isCancelled) {
            const allRequests = data.consultations || [];
            // Filter for general template requests only
            const newRequests = allRequests.filter((r: PendingRequest) => 
              r.metadata?.templateType === 'general' || r.caseType === 'general_inquiry'
            );
            
            // Check for new requests and play sound using ref to avoid dependency
            const newRequestIds = new Set<string>(newRequests.map((r: PendingRequest) => r.requestId));
            const hasNewRequests = newRequests.some((r: PendingRequest) => !currentRequestIdsRef.current.has(r.requestId));
            if (hasNewRequests && !isInitial && currentRequestIdsRef.current.size > 0) {
              console.log('🔔 AGENT: New request detected, playing notification sound');
              playNotificationSound();
            }
            
            // Update current request IDs ref
            currentRequestIdsRef.current = newRequestIds;
            setPendingRequests(newRequests);
          }
        } else if (response.status === 404) {
          console.log('📭 AGENT: No pending requests found');
          if (!isCancelled) {
            currentRequestIdsRef.current = new Set();
            setPendingRequests([]);
          }
        } else {
          const errorText = await response.text();
          console.error('🔴 AGENT: Error fetching requests:', errorText);
          if (!isCancelled) {
            setError(`Failed to fetch requests: ${response.status}`);
          }
        }
      } catch (err) {
        console.error('🔴 AGENT: Network error fetching requests:', err);
        if (!isCancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch pending requests');
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
          if (isInitial) {
            setIsInitialLoading(false);
          }
        }
      }
    };

    console.log('🔄 AGENT: Setting up initial load and polling for pending requests');
    
    // Initial load
    performFetch(true);
    
    // Set up polling
    const interval = setInterval(() => {
      performFetch(false);
    }, 5000); // Poll every 5 seconds

    return () => {
      console.log('🔄 AGENT: Cleaning up polling interval');
      isCancelled = true;
      clearInterval(interval);
    };
  }, [isAuthenticated, orgId, playNotificationSound]); // Minimal dependencies

  // Format wait time
  const formatWaitTime = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  // Get urgency badge color
  const getUrgencyBadge = (urgency: string) => {
    switch (urgency.toLowerCase()) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isInitialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading pending requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <BrandedHeader 
            showLogo={true}
            showTagline={true}
            size="medium"
          />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-4">Agent Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400">{branding.companyName || companyName} - {agentName}</p>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Pending Requests ({pendingRequests.length})
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {pendingRequests.length === 0 ? 'No pending requests' : `${pendingRequests.length} request${pendingRequests.length !== 1 ? 's' : ''} waiting`}
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`p-2 rounded-lg transition-colors ${
                soundEnabled 
                  ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' 
                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`}
              title={soundEnabled ? 'Disable notifications' : 'Enable notifications'}
            >
              <Volume2 className="w-5 h-5" />
            </button>
            
            <BrandedButton
              onClick={() => fetchPendingRequests(false)}
              disabled={isLoading}
              variant="outline"
              size="small"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Refresh
            </BrandedButton>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Pending Requests List */}
        <div className="space-y-4">
          {pendingRequests.length === 0 ? (
            <BrandedCard padding="large" className="text-center">
              <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Pending Requests
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                All caught up! New requests will appear here automatically.
              </p>
            </BrandedCard>
          ) : (
            pendingRequests.map((request) => (
              <BrandedCard key={request.requestId} padding="large">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="flex items-center space-x-2">
                        <User className="w-5 h-5 text-gray-400" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {request.clientName}
                        </h3>
                      </div>
                      
                      <BrandedBadge variant="primary">
                        General Request
                      </BrandedBadge>
                      
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUrgencyBadge(request.urgency)}`}>
                        {request.urgency.toUpperCase()}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      {/* Contact Info */}
                      {request.metadata.email && (
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                          <FileText className="w-4 h-4 mr-2" />
                          <span>{request.metadata.email}</span>
                        </div>
                      )}
                      
                      {request.metadata.phone && (
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                          <Phone className="w-4 h-4 mr-2" />
                          <span>{request.metadata.phone}</span>
                        </div>
                      )}

                      {/* Wait Time */}
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <Clock className="w-4 h-4 mr-2" />
                        <span>Waiting {formatWaitTime(request.waitTime)}</span>
                      </div>

                      {/* Created Time */}
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <Timer className="w-4 h-4 mr-2" />
                        <span>{new Date(request.createdAt).toLocaleTimeString()}</span>
                      </div>
                    </div>

                    {/* Notes */}
                    {request.metadata.notes && (
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-4">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Notes:</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{request.metadata.notes}</p>
                      </div>
                    )}

                    {/* Request ID for debugging */}
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      Request ID: {request.requestId}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col space-y-2 ml-6">
                    <BrandedButton
                      onClick={() => acceptRequest(request.requestId)}
                      disabled={isAccepting === request.requestId || isRejecting === request.requestId}
                      size="small"
                      className="min-w-[100px]"
                    >
                      {isAccepting === request.requestId ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Accepting...
                        </>
                      ) : (
                        <>
                          <Video className="w-4 h-4 mr-2" />
                          Accept
                        </>
                      )}
                    </BrandedButton>

                    <BrandedButton
                      onClick={() => rejectRequest(request.requestId)}
                      disabled={isAccepting === request.requestId || isRejecting === request.requestId}
                      variant="outline"
                      size="small"
                      className="min-w-[100px]"
                    >
                      {isRejecting === request.requestId ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Rejecting...
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 mr-2" />
                          Decline
                        </>
                      )}
                    </BrandedButton>
                  </div>
                </div>
              </BrandedCard>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
